import { Tag } from '@carbon/react';
import { AUDIT_ACTION } from '@ruby-cms/shared';

// cms_commands.sourceType and audit action labels live in @ruby-cms/shared (the Telegram feeds use them too)
export { AUDIT_ACTION, COMMAND_SOURCE } from '@ruby-cms/shared';

type TagType = 'red' | 'magenta' | 'purple' | 'blue' | 'cyan' | 'teal' | 'green' | 'gray' | 'cool-gray' | 'warm-gray' | 'high-contrast' | 'outline';

const COMMAND_STATUS: Record<string, [string, TagType]> = {
  AWAITING_APPROVAL: ['Chờ duyệt', 'purple'],
  PENDING: ['Đang gửi', 'blue'],
  SUCCEEDED: ['Thành công', 'green'],
  FAILED: ['Thất bại', 'red'],
  REJECTED: ['Bị từ chối', 'gray'],
};

export const COMMAND_TYPE: Record<string, string> = {
  MONEY: 'Tiền', LOCK: 'Khóa tài khoản', CONFIG: 'Cấu hình', RESULT: 'Đặt kết quả', EDIT: 'Sửa kết quả', FLAG: 'Tài khoản thử',
  PLAYER_FLAG: 'Hạn chế người chơi', PASSWORD: 'Đặt lại mật khẩu', REFUND: 'Hoàn cả ván', TRANSFER: 'Chuyển tiền đại lý', JACKPOT: 'Hũ',
  CHAT_DELETE: 'Xóa tin chat', CHAT_SEND: 'Tin hệ thống vào chat', LODE: 'Lô đề', VIP: 'Điểm VIP',
  COOLOFF: 'Tự khóa', POOL_RESET: 'Reset quỹ',
};

export const CONFIG_NAME: Record<string, string> = {
  mkt_dai_ly: 'Mã đại lý MKT',
  an_game: 'Danh sách game ẩn', bao_tri: 'Danh sách game bảo trì', game: 'Hạn mức cược, an toàn game và RTP', dieu_khien: 'Quỹ và bot', noi_dung: 'Nội dung sảnh',
  nap: 'Cấu hình nạp / rút', vong_cuoc: 'Vòng cược trước khi rút', ban_ca: 'Cấu hình Bắn Cá', cong_tac: 'Công tắc hệ thống', chong_lam_dung: 'Chống gian lận', hu: 'Luật hũ (góp, tần suất nổ, x88)',
  chat_ban: 'Từ cấm và nhịp chat',
  lode_ty_le: 'Tỷ lệ trả lô đề', lode_gioi_han: 'Số chặn và trần tiền một số lô đề',
};

// nguon tien thuong co vong cuoc (backend/lib/vong_cuoc.js NGUON)
export const WAGERING_SOURCE: Record<string, string> = {
  giftcode: 'Giftcode', su_kien: 'Thưởng sự kiện / nhiệm vụ', hoan_tra: 'Hoàn trả', den_bu: 'Đền bù', khuyen_mai: 'Khuyến mãi nạp',
};

export function CommandStatus({ s }: { s: string }) {
  const [label, type] = COMMAND_STATUS[s] ?? [s, 'gray'];
  return <Tag size="sm" type={type}>{label}</Tag>;
}

const QUEUE_STATUS: Record<string, [string, TagType]> = {
  PENDING_REVIEW: ['Chờ duyệt', 'purple'],
  APPROVED: ['Đã duyệt', 'blue'],
  REJECTED: ['Từ chối', 'gray'],
  PAID: ['Đã chi', 'green'],
  CANCELLED: ['Hủy', 'warm-gray'],
  EXPIRED: ['Hết hạn', 'cool-gray'],
};

// nap_gd (backend/lib/nap_khop.js): bank transactions and why one is unmatched
const BANK_TX_STATUS: Record<string, [string, TagType]> = {
  chua_khop: ['Chưa khớp', 'red'],
  da_khop: ['Tự khớp', 'green'],
  da_ghep: ['Ghép tay', 'teal'],
  bo_qua: ['Bỏ qua', 'gray'],
};
export const BANK_TX_REASON: Record<string, string> = {
  khong_ma: 'Không có mã lệnh',
  khong_thay_lenh: 'Mã lệnh không tồn tại',
  sai_tk: 'Sai tài khoản nhận',
  sai_so_tien: 'Sai số tiền',
  het_han: 'Về sau khi lệnh hết hạn',
  lenh_da_huy: 'Lệnh đã hủy',
  da_xu_ly: 'Lệnh đã được cộng',
  tu_khoa: 'Người chơi đang tự khóa',
  tai_khoan_da_xoa: 'Tài khoản đã bị xóa',
  // duyet tay khong ma (khach 2026-10-02): dong cua lan duyet, va giao dich ve sau nghi trung voi no
  duyet_khong_ma: 'Duyệt tay không mã giao dịch',
  trung_duyet_tay: 'Nghi trùng lần duyệt tay không mã',
};
export const BANK_TX_SOURCE: Record<string, string> = { sepay: 'Webhook SePay', file: 'Sao kê', tay: 'Nhập khi duyệt' };

export function BankTxStatus({ s }: { s: string }) {
  const [label, type] = BANK_TX_STATUS[s] ?? [s, 'gray'];
  return <Tag size="sm" type={type}>{label}</Tag>;
}

// nap_rut.trang_thai (he A) -> the CMS queue status (api/src/lib/game-db.ts NAP_RUT_TRANG)
const NAP_RUT_QUEUE: Record<string, string> = {
  cho: 'PENDING_REVIEW', da_bao: 'PENDING_REVIEW', dang_tra: 'APPROVED', da_duyet: 'PAID', da_tra: 'PAID', huy: 'REJECTED', nguoi_huy: 'CANCELLED', het_han: 'EXPIRED',
};
export function NapRutStatus({ s }: { s: string }) {
  const q = NAP_RUT_QUEUE[s];
  return q ? <QueueStatus s={q} /> : <Tag size="sm" type="gray">{s || '-'}</Tag>;
}

export function QueueStatus({ s }: { s: string }) {
  const [label, type] = QUEUE_STATUS[s] ?? [s, 'gray'];
  return <Tag size="sm" type={type}>{label}</Tag>;
}

export const HE_A_ERRORS: Record<string, string> = {
  KHONG_DU_TIEN: 'Người chơi không đủ số dư',
  KHUYEN_MAI_CHUA_DU_VONG: 'Người gửi đang nhận khuyến mãi nạp, chưa cược đủ vòng',
  TAI_KHOAN_BI_KHOA: 'Tài khoản người gửi hoặc người nhận đang bị khóa',
  KHONG_CO_NGUOI_CHOI: 'Không có người chơi này',
  NGUOI_NHAN_TU_KHOA: 'Người nhận đang tự khóa, chưa nhận tiền được đến khi hết hạn',
  CHI_DUOC_GIA_HAN: 'Chỉ được gia hạn: lệnh này sẽ rút ngắn tự khóa đang chạy',
  SUPERSEDED: 'Đã có lệnh mới hơn cho người chơi này, lệnh cũ không áp dụng',
  THAM_SO_SAI: 'Tham số sai',
  SAI_KIEU: 'Cấu hình sai định dạng',
  KHOA_DA_DUNG_CHO_LENH_KHAC: 'Khóa lệnh đã dùng cho lệnh khác',
  PHIEN_BAN_CU: 'Cấu hình đã được đổi sau khi lệnh này tạo, lệnh không ghi đè. Tải lại rồi sửa lại',
  GAME_KHONG_HO_TRO: 'Game không nhận đặt kết quả',
  KET_QUA_SAI: 'Kết quả không đúng dạng của game này',
  BAN_SAI: 'Bàn poker không đúng',
  SO_LAN_SAI: 'Số lượt không hợp lệ',
  PHAM_VI_SAI: 'Phạm vi lệnh không hợp lệ',
  GAME_KHONG_CO_HU: 'Game này không có hũ',
  CUA_THANG_KHONG_CUNG_VAN: 'Các cửa này không thể cùng thắng một ván',
  THIEU_LY_DO: 'Thiếu lý do',
  KHONG_CO_LENH: 'Không có lệnh này',
  KHONG_CON_CHO: 'Lệnh không còn lượt chờ',
  CHUA_CO_LENH_DIEU_KHIEN: 'Hệ A chưa có cổng đặt kết quả',
  VAN_DA_QUA: 'Ván đó đã chốt kết quả hoặc đã qua, lệnh không kịp áp. Chốt lại cho ván đang chạy',
  PHIEN_SAI: 'Lệnh ván này chỉ cho game ván chung, một ván',
  KHONG_DOC_DUOC_VAN: 'Không đọc được ván đang chạy, thử lại',
  KHONG_CO_HU: 'Không có hũ này trên máy chủ game',
  HU_AM: 'Hũ sẽ âm sau lệnh này, lệnh không áp',
  DUOI_MOC: 'Hũ sẽ thấp hơn mốc sau lệnh này, lệnh không áp. Hạ mốc trước nếu cần',
  MOC_TREN_HU: 'Mốc mới cao hơn hũ hiện tại, lệnh không áp. Nâng hũ trước',
  CHUA_BIET_MOC: 'Máy chủ chưa nạp hũ này nên chưa biết mốc: mở game một lần hoặc đặt mốc trước',
  HU_CHUA_NAP: 'Máy chủ chưa đọc được hũ từ cơ sở dữ liệu, thử lại',
  SAI_HE: 'Hũ này không thuộc máy chủ nhận lệnh',
  GIA_SAI: 'Giá trị không hợp lệ',
  DELTA_SAI: 'Số cộng / trừ không hợp lệ',
  // hoan ca van (P1-D04)
  VAN_KHONG_CON_MO: 'Ván không còn mở (đã có kết quả hoặc đã hoàn)',
  VAN_DA_CO_KET_QUA: 'Ván đã có kết quả, không hoàn được nữa',
  DANG_TAT: 'Máy chủ game đang tắt, không hoàn được lúc này',
  HOAN_CHUA_XONG: 'Còn khoản chưa hoàn, lệnh được gửi lại tự động',
  HE_B_KHONG_TRA_LOI: 'Hệ B không trả lời, lệnh được gửi lại tự động',
  // lo de (backend/lode/quan_tri.js)
  CAN_HAI_NGUOI: 'Thiếu người tạo hoặc người thực hiện lệnh',
  CHUA_QUAY_XONG: 'Chưa tới giờ có kết quả của miền này',
  BANG_SAI: 'Bảng kết quả sai số giải hoặc số chữ số',
  DAI_KHONG_QUAY: 'Đài này không quay ngày đó',
  DA_CO_KET_QUA: 'Đài đã có kết quả (nguồn vừa về). Dùng Chốt lại nếu cần sửa',
  CHUA_CO_KET_QUA: 'Đài chưa có kết quả, dùng Nhập kết quả',
  KHONG_DOI: 'Bảng mới giống bảng đang dùng',
  KY_DA_HUY: 'Kỳ này đã bị hủy',
  KHONG_CO_VE: 'Không có vé này',
  DA_HOAN: 'Vé đã được hoàn',
  DA_SUA_KET_QUA: 'Vé đã được chủ sở hữu sửa kết quả',
  THU_LAI: 'Vé vừa đổi trạng thái, thử lại',
  CHI_HO_TRO_SQLITE: 'Hệ A không chạy SQLite',
};

export const JACKPOT_ACTION: Record<string, string> = { dat: 'Đặt giá trị', cong: 'Cộng / trừ', dat_lai: 'Đặt lại về mốc', moc: 'Đổi mốc' };

export function AuditAction({ a }: { a: string }) {
  const label = AUDIT_ACTION[a];
  return label ? <span title={a}>{label}</span> : <span className="num">{a}</span>;
}

export function StatusTag({ label, type }: { label: string; type: TagType }) {
  return <Tag size="sm" type={type}>{label}</Tag>;
}
